import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import posthog from 'posthog-js';
import ServerStatus from './components/ServerStatus';
import Hero from './components/Hero';
import ConnectionGuide from './components/ConnectionGuide';
import VisualizePage from './pages/VisualizePage';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import Canvas from './pages/canvas';
import Audit from './pages/audit';
import Connect from './pages/connect';
import Data from './pages/data';
import FileList from './components/FileList';
import CodeViewContainer from './components/CodeViewContainer';
import ElementsGenerator from './components/code/ElementsGenerator';
import TerminalView from './components/TerminalView';
import ConfigurationModal from './components/ConfigurationModal';
import FileManager from './services/FileManager';
import TreeManager from './services/TreeManager';
import FolderManager from './services/FolderManager';
import { scanStandards } from './utils/api';
import featureFlags from './config/versions.config';
import { IdeProvider } from './contexts/IdeContext';
import './App.css';

// V3 Preview View Component
function V3PreviewView({ previewAddress, setPreviewAddress, previewServerActive }) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(previewAddress);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleAddressClick = () => {
    setEditValue(previewAddress);
    setIsEditing(true);
  };

  const handleAddressSave = () => {
    const trimmed = editValue.trim();
    if (trimmed && trimmed !== previewAddress) {
      setPreviewAddress(trimmed);
      localStorage.setItem('previewAddress', trimmed);
    }
    setIsEditing(false);
  };

  const handleAddressKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleAddressSave();
    } else if (e.key === 'Escape') {
      setEditValue(previewAddress);
      setIsEditing(false);
    }
  };

  return (
    <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header - matches ServerStatus UI */}
      <div style={{
        padding: '12px 16px',
        borderBottom: '1px solid #e0e0e0',
        background: '#f5f5f5',
        display: 'flex',
        alignItems: 'center',
        flexShrink: 0
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#333' }}>
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" style={{ width: '18px', height: '18px', color: '#28a745' }}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
          </svg>
          <span>previewing</span>
          {isEditing ? (
            <input
              ref={inputRef}
              type="text"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onBlur={handleAddressSave}
              onKeyDown={handleAddressKeyDown}
              style={{
                padding: '2px 6px',
                fontSize: '14px',
                fontWeight: '600',
                color: '#333',
                border: '1px solid #3B82F6',
                borderRadius: '4px',
                outline: 'none',
                minWidth: '180px',
                fontFamily: 'inherit'
              }}
            />
          ) : (
            <strong
              onClick={handleAddressClick}
              style={{
                color: '#333',
                cursor: 'pointer',
                padding: '2px 6px',
                borderRadius: '4px',
                transition: 'background 0.15s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#f0f0f0'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              title="Click to edit preview address"
            >
              {previewAddress}
            </strong>
          )}
          <button
            onClick={() => {
              const cleared = 'http://localhost:';
              setPreviewAddress(cleared);
              setEditValue(cleared);
              localStorage.setItem('previewAddress', cleared);
            }}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '2px 4px',
              marginLeft: '4px',
              color: '#999',
              fontSize: '14px',
              lineHeight: '1',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '3px'
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = '#666'; e.currentTarget.style.background = '#f0f0f0'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = '#999'; e.currentTarget.style.background = 'transparent'; }}
            title="Clear port"
          >
            ×
          </button>
        </div>
      </div>
      {/* Preview content */}
      <div style={{ flex: 1, overflow: 'hidden' }}>
        {previewServerActive ? (
          <iframe
            src={previewAddress}
            style={{ width: '100%', height: '100%', border: 'none', background: '#fff' }}
            title="Preview"
          />
        ) : (
          <div style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#fafafa',
            color: '#666',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.4 }}>🖥️</div>
            <div style={{ fontSize: '16px', fontWeight: '500', marginBottom: '8px' }}>No preview available</div>
            <div style={{ fontSize: '14px', color: '#666', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span>Start your dev server at</span>
              <input
                type="text"
                value={previewAddress}
                onChange={(e) => {
                  setPreviewAddress(e.target.value);
                  localStorage.setItem('previewAddress', e.target.value);
                }}
                style={{
                  padding: '4px 8px',
                  fontSize: '14px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontFamily: 'monospace',
                  width: '180px',
                  textAlign: 'center'
                }}
                placeholder="http://localhost:3000"
              />
              <span>to see live preview</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function App() {
  const [rootTree, setRootTree] = useState([]);
  const [currentFolderId, setCurrentFolderId] = useState('');
  const [openFiles, setOpenFiles] = useState([]); // Array of { path, lineCount }
  const [activeFile, setActiveFile] = useState(null);
  const [serverStatus, setServerStatus] = useState('checking');
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [previewDrawerOpen, setPreviewDrawerOpen] = useState(false); // Preview drawer state
  const [previewDrawerClosing, setPreviewDrawerClosing] = useState(false); // Track closing animation
  const [isDrawerPeeking, setIsDrawerPeeking] = useState(false); // Peek animation for first-time users
  const [showTerminalHint, setShowTerminalHint] = useState(false); // Hotkey hint for terminal
  const [codeQualityIssues, setCodeQualityIssues] = useState([]);
  const [isLoadingCodeQuality, setIsLoadingCodeQuality] = useState(false);
  const [standardsViolations, setStandardsViolations] = useState([]);
  const [isLoadingStandards, setIsLoadingStandards] = useState(false);
  const [previewAddress, setPreviewAddress] = useState(() => {
    return localStorage.getItem('previewAddress') || 'http://localhost:3000';
  });
  const [previewServerActive, setPreviewServerActive] = useState(false);
  const [terminalOpen, setTerminalOpen] = useState(true); // Terminal panel state - open by default
  const [terminalHeight, setTerminalHeight] = useState(100); // Terminal height in pixels (minimum)
  const [isResizingTerminal, setIsResizingTerminal] = useState(false);
  const [drawerWidth, setDrawerWidth] = useState(100); // Preview drawer width in percentage
  const [isResizingDrawer, setIsResizingDrawer] = useState(false);
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
  const [codeViewFile, setCodeViewFile] = useState(null); // File path for code view (replaces graph when set)
  const [showCodeView, setShowCodeView] = useState(false); // Whether code view is visible
  const [showConnectionGuide, setShowConnectionGuide] = useState(false);
  const previewDrawerRef = useRef(null);
  const terminalDragStartY = useRef(null);
  const terminalDidDrag = useRef(false);

  // Show config modal on first visit (only when server is active, and not in v3 mode)
  useEffect(() => {
    if (serverStatus === 'active' && !featureFlags.showV3) {
      const hasVisited = localStorage.getItem('bonzai_has_visited');
      if (!hasVisited) {
        setIsConfigModalOpen(true);
        localStorage.setItem('bonzai_has_visited', 'true');
      }
    }
  }, [serverStatus]);

  // Peek animation for drawer on first visit (only in v3 mode)
  // Stays peeking until user clicks the handle
  useEffect(() => {
    if (featureFlags.showV3) {
      const hasSeenPeek = localStorage.getItem('bonzai_drawer_peek_seen');
      if (!hasSeenPeek) {
        // Wait for page to settle, then peek and stay open
        const peekDelay = setTimeout(() => {
          setIsDrawerPeeking(true);
        }, 800);
        return () => clearTimeout(peekDelay);
      }
    }
  }, []);

  // Terminal hotkey hint on first visit (only in v3 mode)
  // Shows until user toggles terminal with ⌘B or clicks handle
  useEffect(() => {
    if (featureFlags.showV3) {
      const hasSeenHint = localStorage.getItem('bonzai_terminal_hint_seen');
      if (!hasSeenHint) {
        const hintDelay = setTimeout(() => {
          setShowTerminalHint(true);
        }, 800);
        return () => clearTimeout(hintDelay);
      }
    }
  }, []);

  // Initialize manager instances (stateless utility classes)
  const fileManager = useMemo(() => new FileManager(), []);
  const treeManager = useMemo(() => new TreeManager(), []);
  const folderManager = useMemo(() => new FolderManager(), []);

  // Handle folder click - change scope
  const onFolderClick = useCallback((node) => {
    const newFolderId = folderManager.onFolderClick(node);
    if (newFolderId !== null) {
      setCurrentFolderId(newFolderId);
    }
  }, [folderManager]);

  // Open file in tab
  const openFile = useCallback((node) => {
    const result = fileManager.openFile(node, openFiles, activeFile);
    setOpenFiles(result.openFiles);
    setActiveFile(result.activeFile);
    // Track file open
    const ext = typeof node?.path === 'string' ? node.path.split('.').pop() : 'unknown';
    posthog.capture('file_opened', { file_extension: ext });
  }, [fileManager, openFiles, activeFile]);

  // Close a file tab
  const closeFile = useCallback((path, e) => {
    e.stopPropagation();
    const result = fileManager.closeFile(path, openFiles, activeFile);
    setOpenFiles(result.openFiles);
    setActiveFile(result.activeFile);
  }, [fileManager, openFiles, activeFile]);

  // Update line count for a file
  const updateLineCount = useCallback((path, lineCount) => {
    const updatedFiles = fileManager.updateLineCount(path, lineCount, openFiles);
    setOpenFiles(updatedFiles);
  }, [fileManager, openFiles]);

  // Handle tree update from FileList
  const handleTreeUpdate = useCallback((tree) => {
    const result = treeManager.handleTreeUpdate(tree, currentFolderId);
    setRootTree(result.rootTree);
    // Initialize currentFolderId to root on first load
    if (result.shouldInitializeFolder) {
      setCurrentFolderId('');
    }
  }, [treeManager, currentFolderId]);

  const handleServerStatusChange = useCallback((status) => {
    setServerStatus(status);
  }, []);

  // Handle code quality issues change
  const handleCodeQualityIssuesChange = useCallback((issues) => {
    setCodeQualityIssues(issues);
  }, []);

  // Handle standards scan (triggered manually via button)
  const handleScanStandards = useCallback(async () => {
    let standards = '';
    try {
      const saved = localStorage.getItem('codeQualityRules');
      if (saved) {
        const rules = JSON.parse(saved);
        standards = rules?.architecture || '';
      }
    } catch (e) {
      console.error('Error loading standards from localStorage:', e);
    }
    
    if (!standards) {
      return;
    }

    setIsLoadingStandards(true);
    try {
      const result = await scanStandards('.', standards);
      const violations = result?.violations || [];
      setStandardsViolations(violations);
    } catch (err) {
      console.error('🔍 [App] Standards scan failed:', err);
    } finally {
      setIsLoadingStandards(false);
    }
  }, []);

  // Standards scan is triggered manually via button (costs tokens)

  // Refresh tree after file operations
  const handleTreeRefresh = useCallback(() => {
    setRefreshTrigger(prev => prev + 1);
  }, []);

  // Check if preview server is running
  useEffect(() => {
    const checkPreviewServer = async () => {
      try {
        const response = await fetch(previewAddress, { method: 'HEAD', mode: 'no-cors' });
        setPreviewServerActive(true);
      } catch (error) {
        setPreviewServerActive(false);
      }
    };
    
    checkPreviewServer();
    const interval = setInterval(checkPreviewServer, 3000);
    return () => clearInterval(interval);
  }, [previewAddress]);

  // Handle preview drawer close with animation
  const handlePreviewDrawerClose = useCallback(() => {
    setPreviewDrawerClosing(true);
    setTimeout(() => {
      setPreviewDrawerOpen(false);
      setPreviewDrawerClosing(false);
    }, 300); // Match animation duration
  }, []);

  // Handle preview drawer toggle via handle click
  const handlePreviewDrawerHandleClick = useCallback(() => {
    // Mark peek as seen when user clicks handle
    if (isDrawerPeeking) {
      setIsDrawerPeeking(false);
      localStorage.setItem('bonzai_drawer_peek_seen', 'true');
    }
    if (previewDrawerOpen) {
      handlePreviewDrawerClose();
    } else {
      setPreviewDrawerOpen(true);
      posthog.capture('preview_drawer_opened', { method: 'click' });
    }
  }, [previewDrawerOpen, handlePreviewDrawerClose, isDrawerPeeking]);

  // Keyboard shortcut: Cmd+G to toggle preview drawer
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Cmd+G (Mac) or Ctrl+G (Windows/Linux) to toggle preview drawer
      if ((e.metaKey || e.ctrlKey) && e.key === 'g') {
        e.preventDefault(); // Prevent browser's "Find" dialog
        // Dismiss peek if active
        if (isDrawerPeeking) {
          setIsDrawerPeeking(false);
          localStorage.setItem('bonzai_drawer_peek_seen', 'true');
        }
        if (previewDrawerOpen) {
          handlePreviewDrawerClose();
        } else {
          setPreviewDrawerOpen(true);
          posthog.capture('preview_drawer_opened', { method: 'keyboard' });
        }
      }
      // Cmd+B (Mac) or Ctrl+B (Windows/Linux) to toggle terminal drawer
      if ((e.metaKey || e.ctrlKey) && e.key === 'b') {
        e.preventDefault();
        // Dismiss terminal hint if active
        if (showTerminalHint) {
          setShowTerminalHint(false);
          localStorage.setItem('bonzai_terminal_hint_seen', 'true');
        }
        setTerminalOpen(prev => {
          if (!prev) posthog.capture('terminal_toggled', { method: 'keyboard', action: 'open' });
          return !prev;
        });
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [previewDrawerOpen, handlePreviewDrawerClose, isDrawerPeeking, showTerminalHint]);

  // Handle terminal resize drag
  useEffect(() => {
    if (!isResizingTerminal) return;

    // Prevent text selection while dragging
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'ns-resize';
    terminalDidDrag.current = false;

    const handleMouseMove = (e) => {
      terminalDidDrag.current = true;
      const windowHeight = window.innerHeight;
      const newHeight = windowHeight - e.clientY;
      // Clamp between 100px and 80% of window height
      const clampedHeight = Math.max(100, Math.min(newHeight, windowHeight * 0.8));
      setTerminalHeight(clampedHeight);
    };

    const handleMouseUp = () => {
      setIsResizingTerminal(false);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
      // Reset drag flag after a brief delay to prevent click from firing
      setTimeout(() => {
        terminalDidDrag.current = false;
      }, 50);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
  }, [isResizingTerminal]);

  // Handle drawer resize drag
  useEffect(() => {
    if (!isResizingDrawer) return;

    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'ew-resize';

    const handleMouseMove = (e) => {
      const windowWidth = window.innerWidth;
      const newWidth = ((windowWidth - e.clientX) / windowWidth) * 100;
      const clampedWidth = Math.max(30, Math.min(newWidth, 100));
      setDrawerWidth(clampedWidth);
    };

    const handleMouseUp = () => {
      setIsResizingDrawer(false);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
  }, [isResizingDrawer]);

  // When showConfigOnly is true, render only the config page
  if (featureFlags.showConfigonly) {
    return (
      <div className="App">
        <ConfigurationModal
          isOpen={true}
          onClose={() => {}}
          fullScreen={true}
        />
      </div>
    );
  }

  // Main app content (graph, terminal, etc.)
  const mainContent = (
        <>
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column',
        width: '100%', 
        height: 'calc(100vh - 50px)',
        position: 'relative',
        zIndex: 1
      }}>
        {/* Main Content Area */}
        <div style={{
          display: 'flex',
          flex: 1,
          minHeight: 0,
          height: '100%'
        }}>
        <div className="graph-container" style={{
          flex: 1,
          height: '100%',
          position: 'relative',
          overflow: 'hidden',
          marginLeft: '280px'
        }}>
          <FileList
            onFileClick={openFile}
            onFolderClick={onFolderClick}
            onTreeUpdate={handleTreeUpdate}
            refreshTrigger={refreshTrigger}
          />
          <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div style={{ flex: 1, height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
              {/* Main Content Area - Graph when v3, Preview otherwise */}
              <div style={{
                flex: 1,
                position: 'relative',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column'
              }}>
                {featureFlags.showV3 ? (
                  <>
                    {/* Chart View - shown when code view is not active */}
                    <div style={{
                      width: '100%',
                      height: '100%',
                      display: !showCodeView ? 'block' : 'none'
                    }}>
                      <ElementsGenerator
                        rootTree={rootTree}
                        onTreeRefresh={handleTreeRefresh}
                        onOpenCodeView={(file) => {
                          setCodeViewFile(file);
                          setShowCodeView(true);
                          const ext = typeof file === 'string' ? file.split('.').pop() : 'unknown';
                          posthog.capture('code_view_opened', { file_extension: ext });
                        }}
                      />
                    </div>
                    {/* Code View - always mounted to preserve state, visibility controlled via CSS */}
                    <div style={{
                      width: '100%',
                      height: '100%',
                      display: showCodeView ? 'block' : 'none'
                    }}>
                      <CodeViewContainer
                        rootTree={rootTree}
                        initialFile={codeViewFile}
                        onClose={() => {
                          setShowCodeView(false);
                          setCodeViewFile(null);
                        }}
                      />
                    </div>

                    {/* Preview Drawer Handle - Always visible on right edge */}
                    <div
                      onClick={handlePreviewDrawerHandleClick}
                      onMouseDown={(e) => {
                        if (previewDrawerOpen && !previewDrawerClosing) {
                          e.preventDefault();
                          e.stopPropagation();
                          setIsResizingDrawer(true);
                        }
                      }}
                      style={{
                        position: 'absolute',
                        right: (previewDrawerOpen && !previewDrawerClosing) ? `${drawerWidth}%` : isDrawerPeeking ? '23.75%' : '0',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        width: '20px',
                        height: '80px',
                        background: '#e0e0e0',
                        borderTopLeftRadius: '8px',
                        borderBottomLeftRadius: '8px',
                        borderTopRightRadius: '0',
                        borderBottomRightRadius: '0',
                        cursor: (previewDrawerOpen && !previewDrawerClosing) ? 'ew-resize' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1001,
                        transition: isResizingDrawer ? 'none' : 'right 0.3s ease-out, background 0.2s ease, box-shadow 0.3s ease-out',
                        boxShadow: (previewDrawerOpen && !previewDrawerClosing) || isDrawerPeeking ? '-2px 0 4px rgba(0, 0, 0, 0.1)' : '2px 0 4px rgba(0, 0, 0, 0.1)'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#d0d0d0';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = '#e0e0e0';
                      }}
                    >
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '4px',
                        opacity: 0.6
                      }}>
                        <div style={{ width: '3px', height: '3px', background: '#666', borderRadius: '50%' }} />
                        <div style={{ width: '3px', height: '3px', background: '#666', borderRadius: '50%' }} />
                        <div style={{ width: '3px', height: '3px', background: '#666', borderRadius: '50%' }} />
                      </div>
                    </div>

                    {/* Preview Drawer - Slides in from right */}
                    <div
                      ref={previewDrawerRef}
                      style={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        width: `${drawerWidth}%`,
                        height: '100%',
                        background: '#fff',
                        boxShadow: (previewDrawerOpen && !previewDrawerClosing) || isDrawerPeeking ? '-4px 0 12px rgba(0, 0, 0, 0.15)' : 'none',
                        zIndex: (previewDrawerOpen || previewDrawerClosing || isDrawerPeeking) ? 1000 : -1,
                        display: 'flex',
                        flexDirection: 'column',
                        transform: (previewDrawerOpen && !previewDrawerClosing) ? 'translateX(0)' : isDrawerPeeking ? 'translateX(75%)' : 'translateX(100%)',
                        transition: isResizingDrawer ? 'none' : 'transform 0.3s ease-out',
                        pointerEvents: (previewDrawerOpen || previewDrawerClosing || isDrawerPeeking) ? 'auto' : 'none',
                        visibility: (previewDrawerOpen || previewDrawerClosing || isDrawerPeeking) ? 'visible' : 'hidden'
                      }}
                    >
                                            {/* Close Button */}
                      {(previewDrawerOpen && !previewDrawerClosing) && (
                        <div style={{
                          position: 'absolute',
                          right: '16px',
                          top: '12px',
                          zIndex: 1002
                        }}>
                          <div
                            onClick={handlePreviewDrawerClose}
                            style={{
                              cursor: 'pointer',
                              padding: '4px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              opacity: 0.5,
                              transition: 'opacity 0.2s ease'
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.5'; }}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="#666" style={{ width: '20px', height: '20px' }}>
                              <path d="M3.288 4.818A1.5 1.5 0 0 0 1 6.095v7.81a1.5 1.5 0 0 0 2.288 1.276l6.323-3.905c.155-.096.285-.213.389-.344v2.973a1.5 1.5 0 0 0 2.288 1.276l6.323-3.905a1.5 1.5 0 0 0 0-2.552l-6.323-3.906A1.5 1.5 0 0 0 10 6.095v2.972a1.506 1.506 0 0 0-.389-.343L3.288 4.818Z" />
                            </svg>
                          </div>
                        </div>
                      )}
                      {/* Hotkey hint when peeking */}
                      {isDrawerPeeking && (
                        <div style={{
                          position: 'absolute',
                          left: 0,
                          top: 0,
                          width: '25%',
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px',
                          pointerEvents: 'none',
                          zIndex: 1003
                        }}>
                          <span style={{
                            color: '#666',
                            fontSize: '14px',
                            fontWeight: 500
                          }}>Press</span>
                          <div style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            padding: '8px 12px',
                            background: '#f5f5f5',
                            border: '1px solid #d0d0d0',
                            borderRadius: '6px',
                            boxShadow: '0 2px 0 #c0c0c0',
                            fontFamily: 'system-ui, -apple-system, sans-serif',
                            fontSize: '16px',
                            fontWeight: 600,
                            color: '#333'
                          }}>
                            <span>⌘</span>
                            <span>G</span>
                          </div>
                          <span style={{
                            color: '#666',
                            fontSize: '14px',
                            fontWeight: 500
                          }}>to preview</span>
                        </div>
                      )}
                      {/* Drawer Content */}
                      <div style={{ flex: 1, overflow: 'hidden' }}>
                        <V3PreviewView
                          previewAddress={previewAddress}
                          setPreviewAddress={setPreviewAddress}
                          previewServerActive={previewServerActive}
                        />
                      </div>
                    </div>
                  </>
                ) : previewServerActive ? (
                  <iframe
                    src={previewAddress}
                    style={{
                      width: '100%',
                      height: '100%',
                      border: 'none',
                      background: '#fff'
                    }}
                    title="Preview"
                  />
                ) : (
                  <div style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: '#fafafa',
                    color: '#666',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                  }}>
                    <div style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.4 }}>🖥️</div>
                    <div style={{ fontSize: '16px', fontWeight: '500', marginBottom: '8px' }}>No preview available</div>
                    <div style={{ fontSize: '13px', color: '#888', marginBottom: '12px' }}>
                      <code style={{ background: '#e8e8e8', padding: '2px 6px', borderRadius: '4px' }}>npx bonzai-tree</code> has started architecture view from <code style={{ background: '#e8e8e8', padding: '2px 6px', borderRadius: '4px' }}>http://localhost:6767</code>
                    </div>
                    <div style={{ fontSize: '14px', color: '#3B82F6' }}>
                      Start your dev server at <code style={{ background: '#e8e8e8', padding: '2px 6px', borderRadius: '4px' }}>{previewAddress}</code> to see live preview
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Branch Selector Panel - hidden when v3 enabled */}
        </div>
      </div>
        </>
  );

  return (
    <IdeProvider>
    <BrowserRouter>
      <Routes>
        <Route path="/" element={
          <div className="App">
            <Hero variant="default" />
          </div>
        } />
        <Route path="/hn1" element={
          <div className="App">
            <Hero
              variant="hn1"
              tagline="Repo visualization for AI coding"
              headline="See what the AI is building."
              subtext="AI agents write code fast, but keeping up is a different story. Get a bird's eye view of your repo as it changes."
            />
          </div>
        } />
        <Route path="/vcn1" element={
          <div className="App">
            <Hero
              variant="vcn1"
              tagline="Made for vibe coders"
              headline="Vibe coding from a bird's eye view."
              subtext="AI writes fast, but keeping up is a different story - maybe it's time for another perspective."
              accentColor="#8b5cf6"
            />
          </div>
        } />
        <Route path="/visualize" element={
          <div className="App">
            <div style={{ display: serverStatus === 'inactive' ? 'none' : 'block' }}>
              <ServerStatus
                onStatusChange={handleServerStatusChange}
                codeQualityIssues={codeQualityIssues}
                isLoadingCodeQuality={isLoadingCodeQuality}
                standardsViolations={standardsViolations}
                isLoadingStandards={isLoadingStandards}
                onScanStandards={handleScanStandards}
                onAddressChange={setPreviewAddress}
                isCodeViewActive={showCodeView}
                isDevViewActive={previewDrawerOpen}
                isChartActive={!showCodeView && !previewDrawerOpen}
                onToggleCodeView={() => {
                  setShowCodeView(true);
                  handlePreviewDrawerClose();
                }}
                onToggleDevView={() => {
                  setPreviewDrawerOpen(true);
                  setShowCodeView(false);
                  setCodeViewFile(null);
                }}
                onToggleChart={() => {
                  setShowCodeView(false);
                  setCodeViewFile(null);
                  handlePreviewDrawerClose();
                }}
                previewServerActive={previewServerActive}
                terminalOpen={terminalOpen}
                onToggleTerminal={() => setTerminalOpen(prev => !prev)}
              />
            </div>
            <VisualizePage serverStatus={serverStatus}>
              {mainContent}
            </VisualizePage>
            <ConfigurationModal
              isOpen={isConfigModalOpen}
              onClose={() => setIsConfigModalOpen(false)}
            />
          </div>
        } />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/canvas" element={<Canvas />} />
        <Route path="/audit" element={<Audit />} />
        <Route path="/connect" element={<Connect />} />
        <Route path="/data" element={<Data />} />
      </Routes>
    </BrowserRouter>
    </IdeProvider>
  );
}

export default App;

